import React from 'react';
import {
  Form,
  Link,
  useActionData,
  useLoaderData,
  useNavigation,
  useParams,
  useSubmit
} from 'react-router-dom';
import { getCaraTriggerEmailAddress } from '@oysterjs/core/email';
import { type ValidationError } from '@oysterjs/core/graphql';
import { type ActionResult } from '@oysterjs/core/graphql/actions';
import { getValidationError, useFormActions } from '@oysterjs/core/graphql/form';
import { Await } from '@oysterjs/ui/async';
import { PageTitle } from '@oysterjs/ui/page';
import { Button } from '@oysterjs/ui/button';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { MultiListbox, MultiListboxLabel, MultiListboxOption } from '@oysterjs/ui/multiselect';
import { Radio, RadioField, RadioGroup } from '@oysterjs/ui/radio';
import { Textarea } from '@oysterjs/ui/textarea';
import { Select } from '@oysterjs/ui/select';
import { BoltIcon } from '@heroicons/react/24/outline';
import {
  TriggerCallRouting,
  TriggerCallType,
  TriggerEventType,
  type CustomPresetFieldsFragment,
  type TriggerDefinitionFieldsFragment,
  type UpdateTriggerDefinitionInput
} from '~/api/graphql';
import { useOptionalCurrentAgencyUser } from '~/components/current-user';
import { FormError } from '~/components/form-error';
import { ProLabel } from '~/components/pro-label';
import { LibraryListEmptyState } from '~/components/list/library-list-empty-state';
import { LibraryListLoading } from '~/components/list/library-list-loading';
import { timezoneOptionsForValue } from '~/lib/agency-timezones';
import { TriggersProUpgradeDialog } from './triggers-upgrade';
import {
  buildDefinitionInput,
  emptyTriggerForm,
  formFromTrigger,
  type TriggerFormState
} from './trigger-form';
import {
  agencyEmailNamespace,
  CALL_ROUTING_LABELS,
  CALL_TYPE_LABELS,
  DAYS_LABELS,
  EMAIL_SUFFIX_PATTERN,
  formatScheduleDays,
  normalizeEmailSuffix,
  ScheduleWeekday,
  scheduleToCron,
  useMailboxLabel
} from './trigger-utils';
import { useTriggersEntitlement } from './use-triggers-entitlement';
import { TriggerTaskField, taskUseCase } from './trigger-task-picker';
import { CreationEventContext } from '~/components/triggers/email-builder/creation-event';
import { LegacyCreationEditor } from '~/components/triggers/email-builder/creation-legacy';
import { TriggerEmailCreatePage } from '~/pages/workflows/trigger-email-create';

const SAVE_FAILED = 'Unable to save trigger. Please try again.';

const DAYS = [
  ScheduleWeekday.Mon,
  ScheduleWeekday.Tue,
  ScheduleWeekday.Wed,
  ScheduleWeekday.Thu,
  ScheduleWeekday.Fri,
  ScheduleWeekday.Sat,
  ScheduleWeekday.Sun
];

const TEMPLATES = [
  {
    id: 'email',
    name: 'Incoming email',
    description: "Run a preset when an email is forwarded or BCC'd to a Cara address."
  },
  {
    id: 'schedule',
    name: 'Scheduled review',
    description: 'Run a preset on selected days at a configured local time.'
  },
  {
    id: 'call',
    name: 'Call follow-up',
    description: 'Run a preset after a qualifying call completes.'
  }
] as const;

const SectionCard = (props: { title: string; description: string; children: React.ReactNode }) => (
  <div className="rounded-lg bg-white shadow dark:bg-neutral-800">
    <div className="px-4 py-6 sm:px-6">
      <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
        <div>
          <h4 className="mt-0 font-body font-semibold text-neutral-900 dark:text-white">
            {props.title}
          </h4>
          <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
            {props.description}
          </p>
        </div>
        <Fieldset className="col-span-2 flex flex-col gap-y-6">{props.children}</Fieldset>
      </div>
    </div>
  </div>
);

export const TriggersProGate = () => {
  const [showProUpgradeDialog, setShowProUpgradeDialog] = React.useState(false);
  return (
    <>
      <PageTitle
        title="Triggers"
        description="Automatically launch presets from email, schedules, and completed calls."
      >
        <div className="mt-8 rounded-lg bg-white px-4 py-10 text-center shadow dark:bg-neutral-800 sm:px-6">
          <div className="flex justify-center">
            <ProLabel variant="emphasis" />
          </div>
          <h3 className="mt-4 text-base font-semibold text-neutral-900 dark:text-white">
            Triggers are available on Pro
          </h3>
          <p className="mx-auto mt-2 max-w-xl text-sm text-neutral-500 dark:text-neutral-400">
            Upgrade to configure email, schedule, and call-completion triggers that launch Cara
            presets automatically.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Button color="white" href="/app/workflows/triggers">
              Back to triggers
            </Button>
            <Button color="sky" onClick={() => setShowProUpgradeDialog(true)}>
              Learn about Pro
            </Button>
          </div>
        </div>
      </PageTitle>
      <TriggersProUpgradeDialog
        open={showProUpgradeDialog}
        onClose={() => setShowProUpgradeDialog(false)}
      />
    </>
  );
};

export const TriggerEditPage = () => {
  const { triggerId } = useParams<{ triggerId: string }>();
  const isCreate = !triggerId;
  const canManageTriggers = useTriggersEntitlement();
  const loader = useLoaderData() as {
    data: Promise<{
      triggerDefinition?: TriggerDefinitionFieldsFragment | null;
      customPresets: CustomPresetFieldsFragment[];
    }>;
  };

  if (!canManageTriggers) {
    return <TriggersProGate />;
  }

  const page = (children: React.ReactNode) => (
    <PageTitle
      eyebrow="Triggers"
      eyebrowHref="/app/workflows/triggers"
      title={isCreate ? 'Create trigger' : 'Edit trigger'}
      description="Configure an event and the workspace it will launch."
    >
      {children}
    </PageTitle>
  );

  return (
    <React.Suspense
      fallback={page(
        <div className="mt-8">
          <LibraryListLoading title="Loading trigger..." />
        </div>
      )}
    >
      <Await resolve={loader?.data}>
        {(data) =>
          // A mailbox trigger reopens in the editor it was created with.
          data.triggerDefinition?.eventMatcher.emailConfig
            ? page(
                <TriggerEmailCreatePage
                  key={data.triggerDefinition.id}
                  existing={data.triggerDefinition}
                />
              )
            : isCreate || data.triggerDefinition
              ? page(
                  <TriggerEditForm
                    key={data.triggerDefinition?.id ?? 'new'}
                    existing={data.triggerDefinition ?? null}
                    customPresets={data.customPresets ?? []}
                  />
                )
              : page(
                  <div className="mt-8">
                    <LibraryListEmptyState
                      icon={BoltIcon}
                      title="Trigger not found"
                      description="This trigger may have been deleted."
                      action={
                        <Button color="white" href="/app/workflows/triggers">
                          Back to triggers
                        </Button>
                      }
                    />
                  </div>
                )
        }
      </Await>
    </React.Suspense>
  );
};

const EMAIL_SUFFIX_FIELD = ['caraAddressConfig', 'emailSuffix'];

// Errors shown next to their field are left out here so they don't appear twice.
const actionErrorMessage = (action: ActionResult<unknown>): string | null => {
  if (!action) return null;
  return (
    action.applicationError?.message ??
    action.validationErrors?.find((error) => !getValidationError([error], EMAIL_SUFFIX_FIELD))
      ?.message ??
    (action.serverError ? SAVE_FAILED : null)
  );
};

export const TriggerEditForm = ({
  existing,
  customPresets,
  creationEvent
}: {
  existing: TriggerDefinitionFieldsFragment | null;
  customPresets: CustomPresetFieldsFragment[];
  creationEvent?: TriggerEventType;
}) => {
  const isCreate = !existing;
  const submit = useSubmit();
  const navigation = useNavigation();
  const action = useActionData() as ActionResult<unknown>;
  const saveError = actionErrorMessage(action);
  const agencyUser = useOptionalCurrentAgencyUser();
  const emailNamespace = agencyEmailNamespace(agencyUser?.agency);
  const initialForm = React.useMemo(() => {
    if (existing) return formFromTrigger(existing);
    const initial = emptyTriggerForm();
    if (creationEvent) {
      initial.eventType = creationEvent;
      if (creationEvent === TriggerEventType.EmailReceived) initial.emailSuffix = 'intake';
      if (creationEvent === TriggerEventType.CallAnalyzed) {
        initial.callType = TriggerCallType.Inbound;
        initial.callMinimumDurationSeconds = 60;
      }
    }
    return initial;
  }, [existing, creationEvent]);
  const [formState, setForm] = React.useState<TriggerFormState>(initialForm);
  const [creationSubmitted, setCreationSubmitted] = React.useState(false);
  const creationSaveLock = React.useRef(false);
  const creation = isCreate && !!creationEvent;
  const creationName = React.useContext(CreationEventContext)?.name;
  const form = React.useMemo(
    () =>
      creation && creationName !== undefined ? { ...formState, name: creationName } : formState,
    [creation, creationName, formState]
  );
  React.useEffect(() => {
    if (creation && (action?.applicationError || action?.validationErrors?.length)) {
      creationSaveLock.current = false;
      setCreationSubmitted(false);
    }
  }, [action, creation]);
  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const { updateForm } = useFormActions(setForm, setValidationErrors);

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);
  const usesMailboxSource =
    existing?.eventMatcher.eventType === TriggerEventType.EmailReceived &&
    !!existing.eventMatcher.mailboxConfig;
  const mailboxLabel = useMailboxLabel(!usesMailboxSource);

  const timezoneOptions = React.useMemo(
    () => timezoneOptionsForValue(form.scheduleTimezone),
    [form.scheduleTimezone]
  );

  const update = <K extends keyof TriggerFormState>(key: K, value: TriggerFormState[K]) => {
    setForm((previous) => ({ ...previous, [key]: value }));
  };

  const updateScheduleDays = (days: ScheduleWeekday[]) => {
    setForm((previous) => ({
      ...previous,
      scheduleDays: days,
      scheduleCronPassthrough: null
    }));
  };

  const updateScheduleTime = (scheduleTime: string) => {
    setForm((previous) => ({
      ...previous,
      scheduleTime,
      scheduleCronPassthrough: null
    }));
  };

  // Templates leave the name blank so it is always chosen deliberately.
  const applyTemplate = (templateID: string) => {
    const next = emptyTriggerForm();
    if (templateID === 'email') {
      // Forward/BCC model: the alias suffix alone selects this trigger.
      next.eventType = TriggerEventType.EmailReceived;
      next.emailSuffix = 'intake';
    } else if (templateID === 'schedule') {
      next.eventType = TriggerEventType.ScheduleDue;
    } else if (templateID === 'call') {
      next.eventType = TriggerEventType.CallAnalyzed;
      next.callType = TriggerCallType.Inbound;
      next.callMinimumDurationSeconds = 60;
    }
    setForm(next);
    setValidationErrors([]);
  };

  const emailSuffix = normalizeEmailSuffix(form.emailSuffix);
  const emailSuffixInvalid = emailSuffix.length > 0 && !EMAIL_SUFFIX_PATTERN.test(emailSuffix);
  const emailSuffixServerError = getValidationError(validationErrors, EMAIL_SUFFIX_FIELD);

  const emailReady =
    form.eventType !== TriggerEventType.EmailReceived ||
    usesMailboxSource ||
    (emailSuffix.length > 0 && !emailSuffixInvalid && emailNamespace.length > 0);

  const scheduleReady = (() => {
    if (form.eventType !== TriggerEventType.ScheduleDue || form.scheduleCronPassthrough)
      return true;
    // Draft fields may be incomplete; keep the strict serializer as the submit gate.
    try {
      scheduleToCron(form.scheduleTime, form.scheduleDays);
      return true;
    } catch {
      return false;
    }
  })();

  const selectedUseCase = taskUseCase(form.task);
  const instructionsRequired = selectedUseCase?.requires === 'instructions';

  const canSave =
    form.name.trim().length > 0 &&
    form.eventType !== null &&
    (form.task !== null || form.prompt.trim().length > 0) &&
    (!instructionsRequired || form.prompt.trim().length > 0) &&
    emailReady &&
    scheduleReady;
  const saving = navigation.state === 'submitting';

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSave || (creation && (creationSaveLock.current || navigation.state !== 'idle'))) return;
    if (creation) {
      creationSaveLock.current = true;
      setCreationSubmitted(true);
    }
    const input = buildDefinitionInput(form, existing);
    if (existing) {
      const updateInput: UpdateTriggerDefinitionInput = {
        id: existing.id,
        version: existing.version,
        ...input
      };
      submit(updateInput, { method: 'PUT', encType: 'application/json' });
    } else {
      submit(input, { method: 'POST', encType: 'application/json' });
    }
  };

  if (creation) {
    return (
      <LegacyCreationEditor
        form={form}
        onFieldChange={update}
        onEmailSuffixChange={(emailSuffix) =>
          updateForm(EMAIL_SUFFIX_FIELD, (previous) => ({ ...previous, emailSuffix }))
        }
        onScheduleDaysChange={updateScheduleDays}
        onScheduleTimeChange={updateScheduleTime}
        onSubmit={handleSubmit}
        canSave={canSave}
        scheduleReady={scheduleReady}
        saving={navigation.state !== 'idle' || creationSubmitted}
        validationErrors={validationErrors}
        saveError={
          creationSubmitted && action?.serverError
            ? 'The save result is unknown. Check the trigger list before trying again; this editor will not replay the save. Your draft is preserved.'
            : saveError
        }
        customPresets={customPresets}
        emailNamespace={emailNamespace}
        emailSuffixInvalid={emailSuffixInvalid}
        emailSuffixServerError={emailSuffixServerError}
        dirty={JSON.stringify({ ...form, name: initialForm.name }) !== JSON.stringify(initialForm)}
      />
    );
  }

  return (
    <>
      <Form className="mt-8 space-y-6" onSubmit={handleSubmit}>
        {isCreate ? (
          <div className="rounded-lg bg-white shadow dark:bg-neutral-800">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="m-0 text-lg font-medium text-neutral-900 dark:text-white">
                Start with a template
              </h3>
              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                Choose an event pattern to get started quickly.
              </p>
              <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-3">
                <Link
                  to="/app/workflows/triggers/new/email"
                  className="rounded-lg border border-neutral-200 p-4 text-left hover:border-primary-300 dark:hover:border-primary-500/30 hover:bg-primary-50/50 dark:border-neutral-700 dark:hover:bg-neutral-700"
                >
                  <p className="m-0 text-sm font-semibold text-neutral-900 dark:text-white">
                    Connected mailbox email
                  </p>
                  <p className="mt-1 text-xs/5 text-neutral-500 dark:text-neutral-400">
                    Monitor eligible arrivals in your connected Outlook mailboxes.
                  </p>
                </Link>
                {TEMPLATES.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => applyTemplate(template.id)}
                    className="rounded-lg border border-neutral-200 p-4 text-left hover:border-primary-300 dark:hover:border-primary-500/30 hover:bg-primary-50/50 dark:border-neutral-700 dark:hover:bg-neutral-700"
                  >
                    <p className="m-0 text-sm font-semibold text-neutral-900 dark:text-white">
                      {template.name}
                    </p>
                    <p className="mt-1 text-xs/5 text-neutral-500 dark:text-neutral-400">
                      {template.description}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        <SectionCard
          title="Basic information"
          description="Name this trigger so you can identify it in the triggers list."
        >
          <FieldGroup>
            <Field className="max-w-md">
              <Label>Name</Label>
              <Description>A short label shown in the triggers list and executions.</Description>
              <Input
                value={form.name}
                onChange={(event) => update('name', event.currentTarget.value)}
              />
            </Field>
          </FieldGroup>
        </SectionCard>

        <SectionCard
          title="When should this run?"
          description="Choose what event will launch this workspace."
        >
          <FieldGroup>
            <Field>
              <Label>Trigger type</Label>
              <RadioGroup
                value={form.eventType}
                onChange={(value) => update('eventType', value as TriggerEventType)}
              >
                <RadioField>
                  <Radio value={TriggerEventType.EmailReceived} color="sky" />
                  <Label>When email arrives</Label>
                  <Description>
                    Match email forwarded or BCC'd to this trigger's Cara alias address (after alias
                    delivery is provisioned).
                  </Description>
                </RadioField>
                <RadioField>
                  <Radio value={TriggerEventType.ScheduleDue} color="sky" />
                  <Label>On a schedule</Label>
                  <Description>Run automatically on selected days at a specific time.</Description>
                </RadioField>
                <RadioField>
                  <Radio value={TriggerEventType.CallAnalyzed} color="sky" />
                  <Label>After a call ends</Label>
                  <Description>
                    Run automatically after a call completes with specific criteria.
                  </Description>
                </RadioField>
              </RadioGroup>
            </Field>

            {form.eventType === TriggerEventType.EmailReceived ? (
              usesMailboxSource && existing?.eventMatcher.mailboxConfig ? (
                <Field className="max-w-md">
                  <Label>Connected mailbox</Label>
                  <Description>
                    This trigger matches mail from a connected mailbox. Source settings are managed
                    with the mailbox integration.
                  </Description>
                  <p className="mt-2 text-sm text-neutral-700 dark:text-neutral-300">
                    {mailboxLabel(existing.eventMatcher.mailboxConfig.integrationId)}
                  </p>
                </Field>
              ) : (
                <Field className="max-w-md">
                  <Label>Email address</Label>
                  <Description>
                    Forward or BCC emails to this address. Cara will automatically include the email
                    content in the workspace.
                  </Description>
                  <Input
                    value={form.emailSuffix}
                    onChange={(event) => {
                      const emailSuffix = event.currentTarget.value;
                      updateForm(EMAIL_SUFFIX_FIELD, (previous) => ({ ...previous, emailSuffix }));
                    }}
                    placeholder="intake"
                    invalid={emailSuffixInvalid || !!emailSuffixServerError}
                  />
                  {emailSuffixInvalid ? (
                    <ErrorMessage>
                      Use lowercase letters and numbers, with single hyphens between words.
                    </ErrorMessage>
                  ) : emailSuffixServerError ? (
                    <ErrorMessage>{emailSuffixServerError}</ErrorMessage>
                  ) : emailNamespace ? (
                    <p className="mt-2 font-mono text-sm text-neutral-700 dark:text-neutral-300">
                      {getCaraTriggerEmailAddress(emailNamespace, emailSuffix)}
                    </p>
                  ) : (
                    <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">
                      Claim an agency email handle in{' '}
                      <a href="/app/settings/agency" className="underline">
                        settings
                      </a>{' '}
                      before creating a Cara address trigger.
                    </p>
                  )}
                </Field>
              )
            ) : null}

            {form.eventType === TriggerEventType.ScheduleDue ? (
              form.scheduleCronPassthrough ? (
                <Field className="max-w-md">
                  <Label>Schedule</Label>
                  <Description>
                    This trigger uses a custom cron expression. Changing days or time below will
                    replace it with a weekly schedule.
                  </Description>
                  <p className="mt-2 font-mono text-sm text-neutral-700 dark:text-neutral-300">
                    {form.scheduleCronPassthrough} · {form.scheduleTimezone}
                  </p>
                  <Button
                    type="button"
                    className="mt-3"
                    color="white"
                    onClick={() =>
                      setForm((previous) => ({
                        ...previous,
                        scheduleCronPassthrough: null,
                        scheduleDays: [
                          ScheduleWeekday.Mon,
                          ScheduleWeekday.Tue,
                          ScheduleWeekday.Wed,
                          ScheduleWeekday.Thu,
                          ScheduleWeekday.Fri
                        ],
                        scheduleTime: '09:00'
                      }))
                    }
                  >
                    Convert to weekly schedule
                  </Button>
                </Field>
              ) : (
                <>
                  <Field className="max-w-md">
                    <Label>Days of the week</Label>
                    <MultiListbox
                      value={form.scheduleDays}
                      onChange={updateScheduleDays}
                      placeholder="Select days..."
                    >
                      {DAYS.map((day) => (
                        <MultiListboxOption key={day} value={day}>
                          <MultiListboxLabel>{DAYS_LABELS[day]}</MultiListboxLabel>
                        </MultiListboxOption>
                      ))}
                    </MultiListbox>
                  </Field>
                  <Field>
                    <Label>Time and timezone</Label>
                    <Description>Choose when this trigger should run each day</Description>
                    <div data-slot="control" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div data-slot="control">
                        <Input
                          type="time"
                          value={form.scheduleTime}
                          onChange={(event) => updateScheduleTime(event.currentTarget.value)}
                        />
                      </div>
                      <div data-slot="control">
                        <Select
                          value={form.scheduleTimezone}
                          onChange={(event) =>
                            update('scheduleTimezone', event.currentTarget.value)
                          }
                        >
                          {timezoneOptions.map((tz) => (
                            <option key={tz.value} value={tz.value}>
                              {tz.label}
                            </option>
                          ))}
                        </Select>
                      </div>
                    </div>
                  </Field>
                  {form.scheduleDays.length > 0 && form.scheduleTime ? (
                    <div className="rounded-md bg-sky-50 px-3 py-2 text-sm text-sky-900 dark:bg-sky-900/20 dark:text-sky-100">
                      Runs every <strong>{formatScheduleDays(form.scheduleDays)}</strong> at{' '}
                      <strong>{form.scheduleTime}</strong> {form.scheduleTimezone}
                    </div>
                  ) : null}
                </>
              )
            ) : null}

            {form.eventType === TriggerEventType.CallAnalyzed ? (
              <>
                <Field className="max-w-md">
                  <Label>Trigger on</Label>
                  <Select
                    value={form.callType}
                    onChange={(event) =>
                      update('callType', event.currentTarget.value as TriggerCallType)
                    }
                  >
                    {Object.values(TriggerCallType).map((callType) => (
                      <option key={callType} value={callType}>
                        {CALL_TYPE_LABELS[callType]}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field className="max-w-md">
                  <Label>Customer type</Label>
                  <Select
                    value={form.callRouting}
                    onChange={(event) =>
                      update('callRouting', event.currentTarget.value as TriggerCallRouting)
                    }
                  >
                    {Object.values(TriggerCallRouting).map((routing) => (
                      <option key={routing} value={routing}>
                        {CALL_ROUTING_LABELS[routing]}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field className="max-w-xs">
                  <Label>Minimum call length</Label>
                  <Description>Only trigger if the call lasted at least this long</Description>
                  <div data-slot="control" className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={0}
                      value={String(Math.floor(form.callMinimumDurationSeconds / 60))}
                      onChange={(event) =>
                        update(
                          'callMinimumDurationSeconds',
                          (Number(event.currentTarget.value) || 0) * 60
                        )
                      }
                      className="w-20"
                    />
                    <span className="text-sm text-neutral-600 dark:text-neutral-400">minutes</span>
                  </div>
                </Field>
              </>
            ) : null}
          </FieldGroup>
        </SectionCard>

        <SectionCard
          title="What should Cara do?"
          description="Choose a preset, write custom instructions, or combine both."
        >
          <FieldGroup>
            {/* Not a Field: Headless would label the picker's search box as "Preset". */}
            <div>
              <span className="text-base/6 font-medium text-neutral-950 sm:text-sm/6 dark:text-white">
                Preset (optional)
              </span>
              <p className="mb-2 mt-1 text-base/6 text-neutral-600 sm:text-sm/6 dark:text-neutral-400">
                Start from a built-in preset or one your agency made.
              </p>
              <TriggerTaskField
                value={form.task}
                customPresets={customPresets}
                onChange={(task) => update('task', task)}
              />
            </div>
            <Field>
              <Label>
                {selectedUseCase?.instructions.label ?? 'Custom instructions'}
                {!instructionsRequired && ' (optional)'}
              </Label>
              <Description>
                {form.task ? 'Added to the preset’s own instructions.' : 'What Cara should do.'}{' '}
                {form.eventType === TriggerEventType.EmailReceived
                  ? 'The incoming email will be included automatically.'
                  : ''}
              </Description>
              <Textarea
                rows={4}
                value={form.prompt}
                onChange={(event) => update('prompt', event.currentTarget.value)}
                placeholder={
                  selectedUseCase?.instructions.placeholder ??
                  'Generate a COI for the attached request.'
                }
              />
            </Field>
          </FieldGroup>
        </SectionCard>

        <FormError message={saveError} />

        <div className="flex justify-end gap-3">
          <Button color="white" href="/app/workflows/triggers">
            Cancel
          </Button>
          <Button type="submit" color="sky" disabled={!canSave} loading={saving}>
            {isCreate ? 'Create trigger' : 'Save changes'}
          </Button>
        </div>
      </Form>
    </>
  );
};
